import { createTheme } from '@mui/material';
import { colors, fonts } from '@/customComponents/css';

const PropTypes = require('prop-types');
const React = require('react');

/**
 * List of all supported themes.
 */
const themes = [
  {
    id: 'admin',
    theme: colors.admin
  }
];

/**
 * Define a default theme
 */
const defaultTheme = 'admin';

/**
 * Get a theme by providing it's ID
 *
 * @param {string} id Theme id.
 */
export const getTheme = (id) => themes.find((theme) => theme.id === id);

/**
 * Get the default theme.
 */
export const getDefaultTheme = () => themes.find((theme) => theme.id === defaultTheme);

/**
 * Get a list of all theme id's.
 */
export const getThemeIds = () => themes.map((theme) => theme.id);

/**
 * Validate a theme ID to make sure it is one of the supported themes.
 *
 * @param {string} id Theme ID to validate (en-us)
 */
export const isValidTheme = (id) => themes.find((theme) => theme.id === id) !== undefined;

/**
 * Create a context to hold the current theme.
 */
const ThemeContext = React.createContext(getDefaultTheme());
export const TypeProvider = ThemeContext.Provider;
export const TypeConsumer = ThemeContext.Consumer;

/**
 * Create a default MuiGlobal Theme object
 */
export const muiGlobalTheme = createTheme({
  palette: {
    action: { disabled: colors.colorGrey04 },
    primary: { main: colors.colorBlue, dark: colors.colorBlue },
    secondary: { main: colors.colorBlue, dark: colors.colorBlue },
    text: {
      disabled: colors.colorGrey03,
      primary: colors.colorGrey03,
      secondary: colors.colorBlue
    }
  },
  typography: {
    fontFamily: fonts.fontRegular,
    fontSize: 15
  },
  components: {
    MuiIconButton: {
      styleOverrides: {
        root: {
          marginBottom: 0
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&$checked': {
            color: colors.colorBlue
          }
        }
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: { fontSize: 15 }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { fontSize: 12 }
      }
    },
    MuiInput: {
      styleOverrides: {
        underline: {
          '&:before': { borderBottom: `1px solid ${colors.colorGrey04}` },
          '&:hover:not($disabled):not($focused):not($error):before': {
            borderBottom: `1px solid ${colors.colorGrey03}`
          },
          '&:after': { borderBottom: `1px solid ${colors.colorBlue}` },
          '&$disabled:before': {
            borderBottom: ` 1px dotted rgba(0, 0, 0, 0.42) !important;`
          }
        }
      }
    },
    MuiFilledInput: {
      styleOverrides: {
        root: { backgroundColor: colors.colorGrey05 },
        input: {
          color: colors.colorGrey02
        },
        '&$disabled': {
          color: colors.colorGrey02
        },
        underline: {
          '&:before': { borderBottom: 'none' },
          '&:hover:not($disabled):not($focused):not($error):before': {
            borderBottom: 'none'
          },
          '&:after': { borderBottom: 'none' }
        }
      }
    }
  }
});

// MUIv4 Upgrade Styles
// Ideally this will exist in a single theme object in the future, but to accommodate the existing styled-components and multi-theme approach, this is the best solution for now.
export const MUIBaseTheme = createTheme({
  typography: {
    fontFamily: fonts.fontRegular
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '4px 12px',
          fontSize: '13px'
        },
        head: { fontSize: '13px', padding: '16px', color: colors.colorGrey03 }
      }
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          color: colors.colorGrey03
        },
        caption: {
          fontSize: '12px',
          color: colors.colorGrey03
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: colors.admin.midTheme,
          color: colors.admin.lightText,
          margin: '5px'
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        gutters: {
          padding: '11px 16px'
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          padding: '0px 48px'
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: '13px'
        }
      }
    },
    MuiSkeleton: {
      styleOverrides: {
        rect: {
          borderRadius: 4
        }
      }
    },
    MuiInput: {
      styleOverrides: {
        underline: {
          '&:before': {
            borderBottom: `1px solid ${colors.colorGrey04}`
          },
          ':hover:not(.Mui-disabled, .Mui-error):before': {
            borderBottom: `1px solid ${colors.colorGrey03}`
          },
          '&:after': {
            borderBottom: `1px solid ${colors.colorBlue}`
          }
        }
      }
    }
  }
});

/**
 * PropType definition for a theme.
 *
 * You will probably need to use this if you ever access theme on a page's props,
 * pull the active theme from context or use the withActiveTheme HOC.
 */
export const themePropType = PropTypes.shape({
  darkTheme: PropTypes.string.isRequired,
  darkerTheme: PropTypes.string.isRequired,
  darkestTheme: PropTypes.string.isRequired,
  mainTheme: PropTypes.string.isRequired,
  midTheme: PropTypes.string.isRequired,
  highlightTheme: PropTypes.string.isRequired,
  lightTheme: PropTypes.string.isRequired,
  lightText: PropTypes.string.isRequired,
  lighterText: PropTypes.string.isRequired
}).isRequired;
